/**
 * Ce qui entoure une contre-étude : son numéro, ses validations, et l'évolution de ses totaux.
 *
 * Trois demandes du métier du 15/08/2026, réunies ici parce qu'elles répondent à la même
 * question — *où en est cette contre-étude ?* :
 *
 *  1. *« il faut donner un statut à une copie en disant V1 V2 V3 ; le conducteur devra pouvoir
 *     modifier laquelle est V1 »* ;
 *  2. *« envoi du mail du conducteur pour avertir responsable transport puis validation […] puis
 *     soumission de la contre-étude et planning au responsable exploitation »* ;
 *  3. *« l'historique des montants totaux des déboursés avec juste dates et montant total, pour
 *     que la personne qui revient sur le déboursé voie rapidement s'il y a eu de grosses
 *     évolutions »*.
 *
 * ## Le bloc est au-dessus du déboursé, replié
 *
 * Le geste quotidien reste de travailler les lignes ; soumettre arrive une fois. Déplié d'office,
 * ce bloc repousserait le tableau sous la ligne de flottaison tous les jours pour un geste
 * occasionnel — mais il annonce dans son titre ce qui attend une réponse, sinon le replier
 * reviendrait à le cacher.
 */
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import { formaterMontant } from "@planif/moteur";
import { chargerSuivi, designerV1, ecrireCommentaireCircuit, soumettreVersion } from "./api";
import type { Circuit, CommentaireCircuit, SuiviVersion } from "./api";
import { ecartEntre } from "./ecart";

/**
 * Ce que chaque circuit attend, en une phrase — un bouton sans explication se clique au hasard.
 *
 * L'ORDRE, lui, n'est plus déclaré ici : il vient du serveur avec les états, parce que c'est une
 * règle métier et non une préférence d'affichage. Deux listes à tenir d'accord finiraient par ne
 * plus l'être, et l'écran montrerait un enchaînement que le serveur refuse.
 */
const EXPLICATION: Record<Circuit, string> = {
  /*
    *Demande de Chris, 02/09/2026 : « rajouter en premier demande de validation envoyée à
    l'assistante pour vérification des paramétrages du chantier, vérification des articles à
    commander et des prix et paramétrage des livraisons ».*

    Trois vérifications nommées, et c'est ce qui distingue cette étape des trois autres : elle
    ne porte pas sur un métier — les matériaux, les acheminements — mais sur ce qui a été SAISI.
    C'est aussi pour ça qu'elle vient en premier : les trois suivantes travaillent sur ces
    données-là.

    « Transmet à l'étape suivante » et non « aux carrières » : sur une version sans matériau du
    groupe, les carrières ne s'appliquent pas et sa validation enchaîne directement vers le
    transport (057). Nommer les carrières ici ferait mentir l'écran une fois sur deux.
  */
  assistante:
    "L'assistante vérifie le paramétrage du chantier, les articles à commander et leurs prix, " +
    "et le paramétrage des livraisons. C'est la première étape, et sa validation transmet " +
    "d'elle-même la demande à l'étape suivante.",
  carrieres:
    "Le responsable carrières vérifie les matériaux du groupe et leurs carrières. Sa validation " +
    "transmet d'elle-même la demande au transport.",
  transport:
    "Le responsable transport vérifie les acheminements dans le planning. Sa réponse vous revient.",
  exploitation:
    "Le responsable exploitation valide la contre-étude et son planning. Son acceptation FIGE la version.",
};

function formaterJour(iso: string): string {
  const [annee, mois, jour] = iso.split("-");
  return `${jour ?? ""}/${mois ?? ""}/${annee ?? ""}`;
}

export function Suivi({
  versionId,
  modifiable,
}: {
  versionId: string;
  modifiable: boolean;
}): JSX.Element | null {
  const [suivi, setSuivi] = useState<SuiviVersion | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);
  /**
   * Le mot d'accompagnement, PAR ÉTAPE.
   *
   * C'était une seule chaîne partagée, et le champ unique qui allait avec était posé après la
   * dernière étape — donc lu comme un second champ d'exploitation (Chris, 02/09/2026). Un mot
   * par étape rend impossible d'envoyer à l'un ce qu'on écrivait pour l'autre.
   */
  const [mots, setMots] = useState<Partial<Record<Circuit, string>>>({});

  const recharger = useCallback((): void => {
    chargerSuivi(versionId)
      .then((r) => {
        setSuivi(r);
        setErreur(null);
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      });
  }, [versionId]);

  useEffect(recharger, [recharger]);

  const agir = (promesse: Promise<unknown>, cle: string): void => {
    setOccupe(cle);
    setErreur(null);
    promesse
      .then(() => {
        // Le mot de l'étape qu'on vient de soumettre se vide — il est parti avec la demande.
        // Ceux des autres étapes restent : `cle` est le circuit soumis, et effacer tout ferait
        // perdre un texte préparé pour l'étape suivante.
        setMots((courant) => ({ ...courant, [cle]: "" }));
        recharger();
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setOccupe(null);
      });
  };

  if (suivi === null) return null;

  /** La dernière demande d'un circuit : c'est elle qui dit où on en est. */
  const derniere = (circuit: Circuit) =>
    suivi.soumissions.find((s) => s.circuit === circuit && s.statut !== "annule");

  const moi = suivi.rangs.find((r) => r.id === versionId);
  const enAttente = suivi.soumissions.filter((s) => s.statut === "en_attente").length;
  const refusee = suivi.soumissions.some((s) => s.statut === "refuse");

  return (
    <section className="suivi">
      <button
        type="button"
        className="suivi__titre"
        onClick={() => {
          setOuvert(!ouvert);
        }}
      >
        {ouvert ? "▾" : "▸"} Validations et historique
        {moi?.rang !== undefined && moi.rang !== null && (
          <span className="suivi__rang">V{String(moi.rang)}</span>
        )}
        {enAttente > 0 && (
          <span className="en-ecart"> · {String(enAttente)} demande(s) en attente</span>
        )}
        {refusee && enAttente === 0 && <span className="en-ecart"> · une demande refusée</span>}
      </button>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      {ouvert && (
        <div className="suivi__corps">
          {/* ---------------------------------------------------------------
              Le numéro de version
              --------------------------------------------------------------- */}
          <div className="suivi__bloc">
            <h4>Numéro de version</h4>
            <p className="note">
              Par défaut, c'est sur la V1 qu'on travaille. Désigner une autre copie{" "}
              <strong>échange</strong> les deux numéros — mais une version figée ou déjà soumise
              garde le sien : sinon « la V1 validée » désignerait demain autre chose.
            </p>
            <ul className="suivi__versions">
              {suivi.rangs.map((v) => (
                <li key={v.id} className={v.id === versionId ? "suivi__version--moi" : undefined}>
                  <strong>{v.rang === null ? "—" : `V${String(v.rang)}`}</strong>{" "}
                  {v.libelle ?? "(sans nom)"}
                  {v.figee && <span className="note"> · figée</span>}
                  {v.soumise && !v.figee && <span className="note"> · soumise</span>}
                  {v.id === versionId && <span className="note"> · celle-ci</span>}
                </li>
              ))}
            </ul>
            {/* `moi === undefined` n'est pas « pas encore V1 » : c'est une version SANS numéro —
                le devis figé, ou la CE de suivi, que la liste des rangs ne contient pas. Le
                bouton s'y affichait et ne pouvait rien faire, en répondant que « le devis figé
                n'est pas une version de travail » à quelqu'un qui regardait sa CE de suivi. */}
            {modifiable && moi !== undefined && moi.rang !== 1 && (
              <button
                type="button"
                disabled={occupe !== null}
                onClick={() => {
                  agir(
                    designerV1(versionId).then(recharger),
                    "v1",
                  );
                }}
              >
                Faire de cette version la V1
              </button>
            )}
          </div>

          {/* ---------------------------------------------------------------
              Les deux circuits
              --------------------------------------------------------------- */}
          <div className="suivi__bloc">
            <h4>Validations — VAL-CE</h4>
            {/*
              L'enchaînement en une phrase, mis à jour pour les QUATRE étapes de la 057.

              Elle disait encore « les carrières d'abord » alors que l'assistante les précède
              depuis le matin du 02/09 : une phrase d'explication qui décrit l'avant-dernière
              version du produit est pire qu'aucune, parce qu'on la croit.
            */}
            <p className="note">
              L'assistante d'abord, puis les carrières, puis le transport —{" "}
              <strong>transmis automatiquement</strong> à chaque validation —, et l'exploitation
              en dernier, que vous soumettez vous-même une fois le transport accepté.
            </p>
            {/*
              CE QUE LE CIRCUIT PRODUIT AU BOUT, et ce qu'il faut avoir franchi pour l'obtenir.

              *Demande de Chris, 02/09/2026 : « pense à inclure que la soumission aux
              administratifs doit être validée pour que le statut soit mis à jour quand on passe
              en CE de suivi ».*

              C'est vrai, et vérifié dans le code : l'acceptation de l'exploitation fige la
              version en `ce_initiale`, crée la CE de suivi et fait basculer le statut du
              chantier, tout dans la même transaction. Et l'étape de l'assistante précède tout
              sans pouvoir être sautée (057) — donc rien de tout cela n'arrive avant sa
              validation.

              Le dire ici plutôt que de le laisser deviner : un conducteur qui voit son chantier
              rester « CE à traiter » cherche la panne dans le statut, alors qu'elle est dans une
              étape qu'il n'a pas encore fait valider.
            */}
            <p className="note">
              <strong>Les quatre validations conditionnent le statut du chantier</strong> :
              l'acceptation de l'exploitation fige cette version, crée la{" "}
              <strong>CE de suivi</strong> et met le statut à jour. Tant que l'assistante n'a pas
              validé, le circuit ne va pas plus loin — le chantier reste donc « CE à traiter »,
              et aucune CE de suivi n'est créée.
            </p>
            {suivi.etats.map((etat) => {
              const circuit = etat.circuit;
              const s = derniere(circuit);
              return (
                <div
                  key={circuit}
                  className={
                    etat.applicable ? "suivi__circuit" : "suivi__circuit suivi__circuit--hors-jeu"
                  }
                >
                  <p>
                    <strong>{suivi.circuits[circuit]}</strong>
                    {!etat.applicable ? (
                      <span className="note"> · ne s'applique pas ici</span>
                    ) : s === undefined ? (
                      <span className="note"> · jamais soumis</span>
                    ) : s.statut === "en_attente" ? (
                      <span className="en-ecart"> · en attente de {s.destinataire.prenom} {s.destinataire.nom}</span>
                    ) : etat.statut === "perime" ? (
                      <span className="en-ecart">
                        {" "}
                        · validé par {s.destinataire.prenom} {s.destinataire.nom}, à refaire depuis
                        le refus
                      </span>
                    ) : s.statut === "accepte" ? (
                      <span className="conforme"> · accepté par {s.destinataire.prenom} {s.destinataire.nom}</span>
                    ) : (
                      <span className="en-ecart"> · refusé — « {s.motif ?? "sans motif"} »</span>
                    )}
                  </p>
                  <p className="note">{EXPLICATION[circuit]}</p>
                  {/*
                    LE MOT D'ACCOMPAGNEMENT, DANS L'ÉTAPE QU'ON SOUMET.

                    *Chris, 02/09/2026 : « au niveau de exploitation il y a deux champs pour
                    laisser des commentaires, tu peux supprimer le deuxième champ un mot pour le
                    responsable, je crois qu'il fait doublon (à vérifier) ».*

                    **Vérifié : ce n'en est pas un.** Ce mot part dans le courriel de validation
                    (« Son message : … ») et s'affiche à l'écran Soumissions en face de la
                    demande ; le commentaire du bloc ci-dessous, lui, vit sur la version et se
                    relit après coup. Le supprimer aurait fait disparaître ce que le responsable
                    lit dans son courriel.

                    Ce qui était faux, c'est sa PLACE : un seul champ, posé après la dernière
                    étape, donc lu comme un second champ d'exploitation. Il descend ici, au-dessus
                    du bouton qui l'emporte, où il ne peut plus désigner autre chose que cette
                    soumission-ci.

                    Une saisie PAR ÉTAPE et non partagée : le circuit n'en ouvre qu'une à la
                    fois, mais un état commun ferait apparaître le même texte sous deux étapes le
                    jour où deux seraient ouvertes — et on l'enverrait sans le vouloir.
                  */}
                  {modifiable && etat.soumissible && (
                    <>
                      <label className="suivi__message">
                        Un mot pour {suivi.circuits[circuit].toLowerCase()} (facultatif) — il part
                        dans son courriel de demande.
                        <textarea
                          rows={2}
                          value={mots[circuit] ?? ""}
                          onChange={(e) => {
                            setMots((courant) => ({ ...courant, [circuit]: e.target.value }));
                          }}
                        />
                      </label>
                      <button
                        type="button"
                        disabled={occupe !== null}
                        onClick={() => {
                          const mot = (mots[circuit] ?? "").trim();
                          agir(soumettreVersion(versionId, circuit, mot === "" ? null : mot), circuit);
                        }}
                      >
                        Soumettre au {suivi.circuits[circuit].toLowerCase()}
                      </button>
                    </>
                  )}
                  {/* Pourquoi il n'y a pas de bouton. Sans cette phrase, une étape sans action se
                      lit comme un écran cassé — et c'est précisément le cas au retour d'un refus,
                      là où le conducteur cherche par où reprendre. */}
                  {modifiable &&
                    !etat.soumissible &&
                    etat.empechement !== null &&
                    etat.statut !== "en_attente" &&
                    etat.statut !== "accepte" && <p className="note">{etat.empechement}</p>}
                  <CommentaireDuCircuit
                    versionId={versionId}
                    circuit={circuit}
                    intitule={suivi.circuits[circuit]}
                    commentaire={suivi.commentaires.find((c) => c.circuit === circuit) ?? null}
                    modifiable={modifiable}
                    onErreur={setErreur}
                    onEnregistre={recharger}
                  />
                </div>
              );
            })}
          </div>

          {/* ---------------------------------------------------------------
              L'historique des totaux
              --------------------------------------------------------------- */}
          <div className="suivi__bloc">
            <h4>Historique des totaux</h4>
            {suivi.historique.length === 0 ? (
              <p className="note">
                Aucun relevé pour l'instant : le premier est pris à la prochaine modification du
                chiffrage.
              </p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Journée</th>
                    <th className="col-nombre">Déboursé</th>
                    <th className="col-nombre">Prix de vente</th>
                    <th className="col-nombre">Écart de déboursé</th>
                  </tr>
                </thead>
                <tbody>
                  {suivi.historique.map((point, rang) => {
                    const veille = rang === 0 ? null : suivi.historique[rang - 1];
                    const delta =
                      veille === null || veille === undefined
                        ? null
                        : ecartEntre(point.debourse, veille.debourse);
                    return (
                      <tr key={point.jour}>
                        <td>{formaterJour(point.jour)}</td>
                        <td className="col-nombre">{formaterMontant(point.debourse)} €</td>
                        <td className="col-nombre">{formaterMontant(point.vente)} €</td>
                        <td className="col-nombre">
                          {delta === null ? (
                            <span className="vide">—</span>
                          ) : (
                            <span className={delta.classe}>
                              {delta.signe}
                              {delta.montant} €
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * Ce que le conducteur a prévu pour UN responsable — la zone de texte en face de son étape.
 *
 * *Demande de Chris, 27/08/2026 : « je voudrais avoir une zone commentaire où le responsable
 * peut expliquer ce qu'il a prévu pour le responsable carrière, pour le responsable transport et
 * pour le responsable exploitation ; ces commentaires pourront être transmis plus tard au chef de
 * chantier ou chef d'équipe ; positionner les zones de texte avant l'historique des totaux, en
 * face de chaque responsable ».*
 *
 * ## Pourquoi il ne se confond pas avec le mot d'une soumission
 *
 * Le mot du bas accompagne un ENVOI et meurt avec lui ; celui-ci vit sur la version, s'écrit en
 * travaillant le chiffrage — souvent bien avant de soumettre — et se relit ensuite. L'écran le
 * dit en toutes lettres : deux champs de texte sur le même bloc, ce sont deux champs qu'on
 * remplira au hasard si rien ne les distingue.
 *
 * ## Il s'enregistre à la SORTIE du champ
 *
 * Comme les référentiels, et pour la raison qui a été apprise le 21/08 : un panneau qui attend un
 * bouton laisse perdre ce qui est saisi, et un bouton par zone en ferait trois de plus sur un
 * bloc qui en a déjà. Le témoin dit ce qui est parti — sans lui, « enregistré à la sortie » est
 * une promesse invérifiable.
 *
 * **Un texte vidé efface la consigne** plutôt que d'en écrire une vide : c'est la règle du 21/08,
 * une donnée absente ne se déguise pas en donnée présente.
 */
function CommentaireDuCircuit({
  versionId,
  circuit,
  intitule,
  commentaire,
  modifiable,
  onErreur,
  onEnregistre,
}: {
  versionId: string;
  circuit: Circuit;
  intitule: string;
  commentaire: CommentaireCircuit | null;
  modifiable: boolean;
  onErreur: (message: string | null) => void;
  /**
   * Relire le suivi après un enregistrement.
   *
   * **Nécessaire, et pas seulement pour rafraîchir une date.** Sans lui, `enregistre` garderait
   * la valeur d'avant : vider le champ après avoir écrit une consigne se comparerait au texte
   * initial, la sortie du champ ne renverrait rien, et l'effacement serait perdu en silence.
   */
  onEnregistre: () => void;
}): JSX.Element | null {
  const enregistre = commentaire?.texte ?? "";
  const [texte, setTexte] = useState(enregistre);
  const [etat, setEtat] = useState<"repos" | "envoi" | "fait">("repos");

  // Le champ suit ce que le serveur a retenu. Le suivi ne se recharge qu'après une action, donc
  // jamais pendant la frappe — et après l'enregistrement, c'est la réponse du serveur qui fait
  // foi, y compris quand elle a rogné les espaces.
  useEffect(() => {
    setTexte(enregistre);
  }, [enregistre]);

  if (!modifiable) {
    // Sur une version figée, un champ vide n'a rien à dire : l'afficher ferait croire à une
    // saisie possible. Une consigne écrite, elle, reste lisible — c'est ce qui a été validé.
    if (enregistre === "") return null;
    return (
      <div className="suivi__commentaire">
        <p className="note">Ce qui était prévu pour le {intitule.toLowerCase()}</p>
        <p className="suivi__commentaire-fige">{enregistre}</p>
      </div>
    );
  }

  return (
    <label className="suivi__commentaire">
      Ce que vous avez prévu pour le {intitule.toLowerCase()} — reste sur la version, et pourra
      être repris pour le chef de chantier.
      <textarea
        rows={2}
        value={texte}
        disabled={etat === "envoi"}
        onChange={(e) => {
          setTexte(e.target.value);
          setEtat("repos");
        }}
        onBlur={() => {
          if (texte.trim() === enregistre) return;
          setEtat("envoi");
          onErreur(null);
          ecrireCommentaireCircuit(versionId, circuit, texte)
            .then(() => {
              setEtat("fait");
              onEnregistre();
            })
            .catch((x: unknown) => {
              setEtat("repos");
              onErreur(x instanceof Error ? x.message : String(x));
            });
        }}
      />
      {etat === "fait" && <span className="conforme"> · enregistré</span>}
      {etat !== "fait" && commentaire !== null && (
        <span className="note">
          {" "}
          · modifié le {formaterJour(commentaire.modifieLe.slice(0, 10))}
          {commentaire.auteur === null ? "" : ` par ${commentaire.auteur}`}
        </span>
      )}
    </label>
  );
}
